import { useEffect, useRef } from 'react';
import JsBarcode from 'jsbarcode';
import { formatINRPaise } from '@/shared/utils/currency';

interface BarcodeLabelProps {
  value: string;
  format?: 'CODE128' | 'EAN13' | 'EAN8' | 'UPC' | 'CODE39';
  name: string;
  price?: number;
  width?: number;
  height?: number;
}

/** Renders one scannable barcode label onto a canvas. CODE128 (the default)
 *  encodes arbitrary alphanumeric text directly, so a product's own SKU can
 *  be used as the barcode value with no separate check-digit scheme to
 *  maintain — EAN13/UPC still need a real 12-13 digit numeric code and are
 *  offered for products that already carry one. */
export function BarcodeLabel({ value, format = 'CODE128', name, price, width = 2, height = 50 }: BarcodeLabelProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const failed = useRef(false);

  useEffect(() => {
    if (!canvasRef.current || !value) return;
    failed.current = false;
    try {
      JsBarcode(canvasRef.current, value, {
        format,
        width,
        height,
        fontSize: 12,
        margin: 4,
        displayValue: true,
      });
    } catch {
      // Invalid value for the chosen format (e.g. non-numeric EAN13) — draw
      // nothing scannable rather than a barcode that silently encodes the
      // wrong thing.
      failed.current = true;
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    }
  }, [value, format, width, height]);

  return (
    <div className="flex flex-col items-center border border-slate-200 dark:border-slate-700 rounded-lg p-2 bg-white break-inside-avoid">
      <p className="text-[11px] font-semibold text-slate-800 text-center truncate w-full">{name}</p>
      <canvas ref={canvasRef} />
      {failed.current && <p className="text-[9px] text-rose-600">Invalid value for {format}</p>}
      {price != null && <p className="text-xs font-bold text-slate-900">{formatINRPaise(price)}</p>}
    </div>
  );
}

export default BarcodeLabel;
